import { useCallback, useEffect, useRef, useState } from 'react';
import {
  initPopstateDispatcher,
  registerViewHandler,
  unregisterViewHandler,
} from './popstateDispatcher';

interface ViewHistoryState<T extends string> {
  navType: 'view';
  view: T;
}

export interface UseViewBackStackResult<T extends string> {
  view: T;
  /** User-driven navigation — pushes a history entry, undoable with back. */
  setView: (next: T) => void;
  /** Non-user-driven correction (e.g. restoring a saved view on auth load) —
   * replaces the current history entry instead of pushing a new one. */
  replaceView: (next: T) => void;
}

/**
 * Wraps a `view` state value so user-driven changes are undoable with the
 * browser back button, mirroring standard SPA router behavior. Also keeps
 * `document.title` in sync with the current view, via an optional
 * `getTitle` callback, covering both the click-driven path and the
 * back/forward path with a single effect keyed on `view`.
 *
 * `getPath` (metrics-deep-linking, MDL-007) maps a view to a real URL path
 * (e.g. `'products'` -> `'/products'`), written via `pushState`/
 * `replaceState`'s URL argument instead of `''` — so the address bar reflects
 * the current view and a plain view switch is itself shareable/bookmarkable.
 * When omitted, behavior is unchanged (URL untouched, state-only). Callers
 * that also need a query string (e.g. a deep-link filter) attach it
 * separately via their own `history.replaceState` call after `setView` —
 * this hook only owns the path, not arbitrary per-navigation payloads.
 */
export function useViewBackStack<T extends string>(
  initialView: T | (() => T),
  getTitle?: (view: T) => string,
  getPath?: (view: T) => string
): UseViewBackStackResult<T> {
  const [view, setViewState] = useState<T>(initialView);
  const getTitleRef = useRef(getTitle);
  getTitleRef.current = getTitle;
  const getPathRef = useRef(getPath);
  getPathRef.current = getPath;
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    const state = { navType: 'view', view } as ViewHistoryState<T>;
    if (getPathRef.current) {
      const path = getPathRef.current(view);
      // Only rewrite the URL if the current pathname doesn't already match
      // the resolved view -- e.g. a boot-time deep link (`/products?filter=
      // ...`) already has the correct pathname, and blindly replacing it
      // with a bare path here would strip its query string on every load
      // (metrics-deep-linking, MDL-007 -- caught via live verification).
      // When the pathname genuinely needs correcting (an unrecognized path
      // that fell back to a saved/default view), it's correct to drop
      // whatever query string belonged to the wrong path.
      if (window.location.pathname !== path) {
        window.history.replaceState(state, '', path);
      } else {
        window.history.replaceState(state, '');
      }
    } else {
      window.history.replaceState(state, '');
    }
    // Intentionally only on mount — captures the initial `view` resolved by
    // useState above, regardless of whether `initialView` was a value or a
    // lazy initializer function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (getTitleRef.current) {
      document.title = getTitleRef.current(view);
    }
  }, [view]);

  useEffect(() => {
    initPopstateDispatcher();
    function handlePop(state: unknown): void {
      const viewState = state as ViewHistoryState<T> | null;
      if (viewState && viewState.navType === 'view') {
        setViewState(viewState.view);
      }
    }
    registerViewHandler(handlePop as (state: unknown) => void);
    return () => unregisterViewHandler(handlePop as (state: unknown) => void);
  }, []);

  const setView = useCallback((next: T) => {
    if (next === viewRef.current) {
      // Already on this view (e.g. clicking the active tab again) — pushing
      // here would create a no-op back step.
      return;
    }
    const state = { navType: 'view', view: next } as ViewHistoryState<T>;
    if (getPathRef.current) {
      window.history.pushState(state, '', getPathRef.current(next));
    } else {
      window.history.pushState(state, '');
    }
    setViewState(next);
  }, []);

  const replaceView = useCallback((next: T) => {
    if (next === viewRef.current) {
      return;
    }
    const state = { navType: 'view', view: next } as ViewHistoryState<T>;
    if (getPathRef.current) {
      window.history.replaceState(state, '', getPathRef.current(next));
    } else {
      window.history.replaceState(state, '');
    }
    setViewState(next);
  }, []);

  return { view, setView, replaceView };
}
